import React, { useState, useEffect, useRef } from 'react'
import { Play, Pause, Loader2, List } from 'lucide-react'
import { ReproducedMixingPlayer } from '../components/ReproducedMixingPlayer'

// Types
interface RealMixProject {
  id: string
  name: string
}

interface TrackInfo {
  pos: number
  start: string // "00:02:23"
  end: string
  artist: string
  title: string
  label: string
  startSeconds: number
  endSeconds: number
}

interface TransitionInfo {
  dirName: string
  transitionId: number
  mixId: string
  trackPrev: string
  trackNext: string
  positionInMix: {
    cueOutTime: number
    cueInTime: number
    transitionLengthTime: number
  }
  trackInfo: {
    prev: {
      artist: string
      title: string
      timestamp: number
    }
    next: {
      artist: string
      title: string
      timestamp: number
    }
  }
  files: {
    video: string
    prevAudio: string
    nextAudio: string
  }
}

// Utility function to parse time string to seconds
const parseTimeToSeconds = (timeStr: string): number => {
  const parts = timeStr.split(':')
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  const seconds = parseInt(parts[2], 10)
  return hours * 3600 + minutes * 60 + seconds
}

// Format seconds to mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
// Main Component
export const RealMixPage: React.FC = () => {
  const [projects, setProjects] = useState<RealMixProject[]>([])
  const [selectedProject, setSelectedProject] = useState<RealMixProject | null>(null)
  const [tracks, setTracks] = useState<TrackInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showTrackList, setShowTrackList] = useState(true)

  // Reproduced Mixing states
  const [transitions, setTransitions] = useState<TransitionInfo[]>([])
  const [selectedTransition, setSelectedTransition] = useState<TransitionInfo | null>(null)
  const [loadingTransitions, setLoadingTransitions] = useState(false)

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null)

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        console.log('🔍 Loading RealMix projects...')
        const response = await fetch('/api/realmix/projects')
        console.log('📡 Response status:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('✅ Projects loaded:', data)
          setProjects(data)
          if (data.length > 0) {
            setSelectedProject(data[0])
            console.log('📂 Selected project:', data[0])
          } else {
            console.warn('⚠️ No projects found in response')
          }
        } else {
          console.error('❌ API response not OK, using fallback')
          // Fallback: Try to load from static content
          setProjects([{ id: '000000', name: 'Fred again.. - Boiler Room: London' }])
          setSelectedProject({ id: '000000', name: 'Fred again.. - Boiler Room: London' })
        }
      } catch (error) {
        console.error('❌ Failed to load projects:', error)
        // Fallback to static data
        setProjects([{ id: '000000', name: 'Fred again.. - Boiler Room: London' }])
        setSelectedProject({ id: '000000', name: 'Fred again.. - Boiler Room: London' })
      } finally {
        setLoading(false)
      }
    }

    loadProjects()
  }, [])

  // Load project data
  useEffect(() => {
    if (!selectedProject) return

    const loadProjectData = async () => {
      try {
        console.log('🎵 Loading project data for:', selectedProject.id)
        // Load trackidnet.csv
        const tracklistUrl = `/content/realmix/${selectedProject.id}/trackidnet.csv`
        console.log('📋 Loading tracklist from:', tracklistUrl)
        
        const response = await fetch(tracklistUrl)
        if (!response.ok) {
          console.error('❌ Failed to load tracklist:', response.status, response.statusText)
          return
        }
        
        const text = await response.text()
        console.log('📄 CSV loaded, length:', text.length)
        
        // Parse CSV
        const lines = text.trim().split('\n')
        const trackData: TrackInfo[] = []
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i]
          // Handle CSV with quoted fields
          const matches = line.match(/(?:^|,)("(?:[^"]|"")*"|[^,]*)/g)
          if (!matches || matches.length < 6) continue
          
          const fields = matches.map(m => m.replace(/^,?"?|"?$/g, '').replace(/""/g, '"'))
          
          trackData.push({
            pos: parseInt(fields[0], 10),
            start: fields[1],
            end: fields[2],
            artist: fields[3],
            title: fields[4],
            label: fields[5],
            startSeconds: parseTimeToSeconds(fields[1]),
            endSeconds: parseTimeToSeconds(fields[2]),
          })
        }
        
        console.log('✅ Parsed tracks:', trackData.length)
        setTracks(trackData)
      } catch (error) {
        console.error('❌ Failed to load project data:', error)
      }
    }

    loadProjectData()
  }, [selectedProject])

  // Load transitions for selected project
  useEffect(() => {
    if (!selectedProject) return

    const loadTransitions = async () => {
      try {
        setLoadingTransitions(true)
        console.log('🔄 Loading transitions for:', selectedProject.id)
        
        const response = await fetch(`/api/realmix/${selectedProject.id}/transitions`)
        if (!response.ok) {
          console.error('❌ Failed to load transitions:', response.status)
          return
        }
        
        const data = await response.json()
        console.log('✅ Transitions loaded:', data.length)
        setTransitions(data)
        
        // Auto-select first transition
        if (data.length > 0) {
          setSelectedTransition(data[0])
        }
      } catch (error) {
        console.error('❌ Failed to load transitions:', error)
      } finally {
        setLoadingTransitions(false)
      }
    }

    loadTransitions()
  }, [selectedProject])

  // Video time update
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      const time = video.currentTime
      
      if (!isNaN(time) && isFinite(time)) {
        setCurrentTime(time)
      }
    }

    const handlePlay = () => {
      console.log('▶️ Video playing')
      setIsPlaying(true)
    }
    
    const handlePause = () => {
      console.log('⏸️ Video paused')
      setIsPlaying(false)
    }
    
    const handleLoadedMetadata = () => {
      const dur = video.duration
      if (!isNaN(dur) && isFinite(dur)) {
        console.log('✅ Video metadata loaded, duration:', dur)
        setDuration(dur)
      } else {
        console.warn('⚠️ Video duration is invalid:', dur)
      }
    }
    
    const handleError = (e: Event) => {
      console.error('❌ Video error:', e)
      const videoElement = e.target as HTMLVideoElement
      if (videoElement.error) {
        console.error('Video error code:', videoElement.error.code)
        console.error('Video error message:', videoElement.error.message)
      }
    }
    
    const handleLoadStart = () => {
      console.log('🎬 Video loading started')
    }
    
    const handleCanPlay = () => {
      console.log('✅ Video can play')
      // Also try to get duration here in case loadedmetadata didn't fire
      const dur = video.duration
      if (!isNaN(dur) && isFinite(dur) && duration === 0) {
        console.log('📏 Setting duration from canplay:', dur)
        setDuration(dur)
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('error', handleError)
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('canplay', handleCanPlay)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('error', handleError)
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('canplay', handleCanPlay)
    }
  }, [duration])


  const togglePlayPause = async () => {
    if (!videoRef.current) return
    
    try {
      if (isPlaying) {
        videoRef.current.pause()
        console.log('⏸️ Video paused by user')
      } else {
        await videoRef.current.play()
        console.log('▶️ Video playing by user')
      }
    } catch (error) {
      console.error('❌ Error toggling play/pause:', error)
      // Reset the isPlaying state if play failed
      if (!isPlaying) {
        setIsPlaying(false)
      }
    }
  }

  const getCurrentTrack = (): TrackInfo | null => {
    return tracks.find(
      track => currentTime >= track.startSeconds && currentTime <= track.endSeconds
    ) || null
  }

  const currentTrack = getCurrentTrack()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">加载 RealMix 项目...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              RealMix Player
            </h1>
            <div className="flex items-center space-x-4">
               {projects.length > 1 && (
                 <select
                   value={selectedProject?.id || ''}
                   onChange={e => {
                     const project = projects.find(p => p.id === e.target.value)
                     if (project) {
                       setSelectedProject(project)
                       // Reset playback state when switching projects
                       setIsPlaying(false)
                       setCurrentTime(0)
                       if (videoRef.current) {
                         videoRef.current.pause()
                         videoRef.current.currentTime = 0
                       }
                     }
                   }}
                   className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                 >
                   {projects.map(project => (
                     <option key={project.id} value={project.id}>
                       {project.name}
                     </option>
                   ))}
                 </select>
               )}
              <button
                onClick={() => setShowTrackList(!showTrackList)}
                className={`p-2 rounded-lg transition-colors ${
                  showTrackList
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
          {selectedProject && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {selectedProject.name}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4">
          {/* Top Row: Set List + Video */}
          <div className="grid grid-cols-12 gap-4">
            {/* Set List */}
            {showTrackList && (
              <div className="col-span-12 lg:col-span-3">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 h-full">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <List className="w-4 h-4 mr-1.5" />
                    Set List (Click to Seek)
                  </h3>
                  
                  <div className="space-y-1.5 max-h-[550px] overflow-y-auto scrollbar-thin">
                    {tracks.map(track => {
                      const isActive =
                        currentTime >= track.startSeconds && currentTime <= track.endSeconds
                      
                      return (
                        <div
                          key={track.pos}
                          onClick={() => {
                            if (videoRef.current) {
                              videoRef.current.currentTime = track.startSeconds
                            }
                          }}
                          className={`p-2 rounded-lg cursor-pointer transition-colors ${
                            isActive
                              ? 'bg-primary-100 dark:bg-primary-900/30 border border-primary-500'
                              : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-0.5">
                            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                              #{track.pos}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {track.start}
                            </span>
                          </div>
                          <div className="font-medium text-xs text-gray-900 dark:text-white mb-0.5 line-clamp-1">
                            {track.title}
                          </div>
                          <div className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-1">
                            {track.artist}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Video Player */}
            <div className={`col-span-12 ${showTrackList ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="aspect-video bg-black relative">
                {selectedProject && (
                  <video
                    ref={videoRef}
                    className="w-full h-full"
                    src={`/content/realmix/${selectedProject.id}/video.mp4`}
                    controls={false}
                  />
                )}
                
                {/* Current Track Overlay */}
                {currentTrack && (
                  <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-white font-semibold">{currentTrack.title}</div>
                    <div className="text-gray-300 text-sm">{currentTrack.artist}</div>
                  </div>
                )}
              </div>

              {/* Video Controls */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center space-x-4 mb-3">
                  <button
                    onClick={togglePlayPause}
                    className="p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full transition-colors"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="0"
                      max={duration || 100}
                      value={currentTime}
                      onChange={e => {
                        const time = parseFloat(e.target.value)
                        if (videoRef.current && !isNaN(time) && isFinite(time)) {
                          videoRef.current.currentTime = time
                          setCurrentTime(time) // Immediate UI update
                        }
                      }}
                      className="w-full appearance-none cursor-pointer bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-lg [&::-webkit-slider-runnable-track]:bg-gray-200 dark:[&::-webkit-slider-runnable-track]:bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:-mt-1 [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-lg [&::-moz-range-track]:bg-gray-200 dark:[&::-moz-range-track]:bg-gray-700 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:border-0"
                      style={{
                        background: duration > 0 
                          ? `linear-gradient(to right, #2563eb 0%, #2563eb ${(currentTime / duration) * 100}%, transparent ${(currentTime / duration) * 100}%, transparent 100%)`
                          : undefined
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-1">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Reproduced Mixing Section */}
          {selectedProject && transitions.length > 0 && (
            <div className="mt-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  对比算法重建的过渡效果
                </h2>

                {/* Transition Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    选择 Transition
                  </label>
                  <select
                    value={selectedTransition?.dirName || ''}
                    onChange={(e) => {
                      const transition = transitions.find(t => t.dirName === e.target.value)
                      setSelectedTransition(transition || null)
                      
                      // Stop main video playback when selecting a transition
                      if (videoRef.current && isPlaying) {
                        videoRef.current.pause()
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                    disabled={loadingTransitions}
                  >
                    {transitions.map((transition) => (
                      <option key={transition.dirName} value={transition.dirName}>
                        Transition #{transition.transitionId}: {transition.trackInfo.prev.artist} - {transition.trackInfo.prev.title} → {transition.trackInfo.next.artist} - {transition.trackInfo.next.title} ({formatTime(transition.positionInMix.cueOutTime)}-{formatTime(transition.positionInMix.cueInTime)})
                      </option>
                    ))}
                  </select>
                  {loadingTransitions && (
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      加载 transitions...
                    </div>
                  )}
                </div>

                {/* Player */}
                {selectedTransition && (
                  <ReproducedMixingPlayer
                    key={selectedTransition.dirName}
                    videoUrl={selectedTransition.files.video}
                    prevAudioUrl={selectedTransition.files.prevAudio}
                    nextAudioUrl={selectedTransition.files.nextAudio}
                    transitionInfo={selectedTransition.trackInfo}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


